class ProductGemPicker extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.productUrl = this.dataset.productUrl;
    this.selections = {};   // { optionName: valueName }
    this.selectedIds = {};  // { optionName: valueId }
    this.positions = {};    // { optionName: position (1,2,3) }
    this.variantCache = {}; // { "id1,id2,id3": { variantId, image, price, available } }

    this.configSlide = null; // <li> that shows the current gemstone-combo photo
    this.gifSlide = null;    // <li> that shows the rotating GIF

    this.querySelectorAll('[data-option-name][data-position]').forEach((group) => {
      this.positions[group.dataset.optionName] = parseInt(group.dataset.position, 10);
    });

    // Pre-populate selections from whichever option value is already marked
    // .is-selected on page load (only Size in practice — Shopify always has
    // a default variant, so Size always shows a pre-selected button).
    // Birthstones never have a default, so they stay unselected until the
    // shopper actually picks one.
    this.querySelectorAll('.is-selected').forEach((btn) => {
      this.selections[btn.dataset.optionName] = btn.dataset.value;
      this.selectedIds[btn.dataset.optionName] = btn.dataset.valueId;
    });

    this.trigger = this.querySelector('.gem-picker-gems-trigger');
    this.triggerTextEl = this.querySelector('.gem-picker-trigger-text');
    this.triggerBadgeEl = this.querySelector('.gem-picker-free-label');
    this.modal = this.querySelector('.gem-picker-modal');
    this.closeBtn = this.querySelector('.gem-picker-modal__close');
    this.doneBtn = this.querySelector('.gem-picker-modal__done');
    this.progressEls = this.querySelectorAll('.gem-picker-progress');
    this.previewImg = this.querySelector('.gem-picker-preview-img');
    this.captionEl = this.querySelector('.gem-picker-preview-caption');

    this.submitButton = document.querySelector('.product-form__submit');
    this.submitLabel = this.submitButton ? this.submitButton.querySelector('span') : null;
    this.originalSubmitText = this.submitLabel ? this.submitLabel.textContent.trim() : null;

    this.trigger.addEventListener('click', () => this.openModal());
    this.closeBtn.addEventListener('click', () => this.closeModal());
    this.doneBtn.addEventListener('click', () => this.closeModal());
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.closeModal();
    });
    this.modal.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.closeModal();
    });

    this.querySelectorAll('.gem-picker-btn, .gem-picker-swatch').forEach((btn) => {
      btn.addEventListener('click', () => this.selectOption(btn));
    });

    this.locateSlides();
    this.showGifOnly();
    this.updateProgress();
    this.updateCaption();
    this.updateTriggerLabel();
    this.updateSubmitState();
  }

  // Finds the two gallery slides we care about:
  // - gifSlide: any slide whose image is a .gif file (works automatically
  //   for any product, no per-product filename configuration needed)
  // - configSlide: whichever slide is marked active on load (the one
  //   showing the default variant's combo photo)
  locateSlides() {
    const imgs = document.querySelectorAll('.product__media img');
    imgs.forEach((img) => {
      const li = img.closest('li');
      if (!li) return;
      const src = img.currentSrc || img.src || '';
      if (src.toLowerCase().includes('.gif')) {
        this.gifSlide = li;
      } else if (li.classList.contains('is-active') && !this.configSlide) {
        this.configSlide = li;
      }
    });
  }

  // Initial state: only the GIF slide visible, the default-combo slide hidden.
  showGifOnly() {
    if (this.configSlide) this.configSlide.classList.add('gem-picker-hidden-slide');
    if (this.gifSlide) this.gifSlide.classList.remove('gem-picker-hidden-slide');
  }

  // Once a full selection is resolved: show the config slide with the new
  // image, hide the GIF slide.
  showConfigSlide(imageSrc) {
    if (this.configSlide) {
      const img = this.configSlide.querySelector('img');
      if (img) {
        img.src = imageSrc;
        img.removeAttribute('srcset');
      }
      this.configSlide.classList.remove('gem-picker-hidden-slide');
    }
    if (this.gifSlide) {
      this.gifSlide.classList.add('gem-picker-hidden-slide');
    }
  }

  openModal() {
    this.modal.showModal();
    requestAnimationFrame(() => this.modal.classList.add('is-open'));
  }

  closeModal() {
    this.modal.classList.remove('is-open');
    setTimeout(() => this.modal.close(), 300);
  }

  selectOption(btn) {
    const name = btn.dataset.optionName;
    const value = btn.dataset.value;
    const valueId = btn.dataset.valueId;

    this.selections[name] = value;
    this.selectedIds[name] = valueId;

    this.querySelectorAll(`[data-option-name="${CSS.escape(name)}"]`).forEach((el) => {
      el.classList.remove('is-selected');
    });
    btn.classList.add('is-selected');

    // Fills the selected value next to the option name (Size legend, and
    // the Birthstone 1 / Birthstone 2 headings in the drawer).
    const label = this.querySelector(`.gem-picker-selected-label[data-selected-for="${CSS.escape(name)}"]`);
    if (label) label.textContent = value;

    this.updateProgress();
    this.updateCaption();
    this.updateTriggerLabel();
    this.updateSubmitState();
    this.resolveVariant();
  }

  updateProgress() {
    const gemGroups = this.querySelectorAll('.gem-picker-gem-group');
    const chosen = Array.from(gemGroups).filter((g) => this.selections[g.dataset.optionName]).length;
    this.progressEls.forEach((el) => {
      el.textContent = `${chosen}/${gemGroups.length}`;
    });
  }

  // Caption shown over the ring preview:
  // - nothing chosen yet     -> "Choose your stones"
  // - only the first chosen  -> "January / -"
  // - both chosen            -> "January / March"
  // - product with 1 gem     -> just the month
  // (Displayed in uppercase via CSS.)
  updateCaption() {
    if (!this.captionEl) return;

    const gemGroups = Array.from(this.querySelectorAll('.gem-picker-gem-group'));
    const values = gemGroups.map((g) => this.selections[g.dataset.optionName] || '');

    if (!values.some(Boolean)) {
      this.captionEl.textContent = this.captionEl.dataset.emptyText || 'Choose your stones';
      return;
    }

    this.captionEl.textContent = values.map((v) => v || '-').join(' / ');
  }

  // Text of the trigger button on the product page:
  // - until every gem is chosen -> the default text ("Choose Your Birthstones")
  // - once every gem is chosen  -> the chosen months ("January / March"),
  //   and the "(Free)" badge is hidden.
  // (Months are displayed in uppercase via CSS.)
  updateTriggerLabel() {
    if (!this.triggerTextEl) return;

    const gemGroups = Array.from(this.querySelectorAll('.gem-picker-gem-group'));
    const values = gemGroups.map((g) => this.selections[g.dataset.optionName] || '');
    const allChosen = values.length > 0 && values.every(Boolean);

    if (allChosen) {
      this.triggerTextEl.textContent = values.join(' / ');
      this.triggerTextEl.classList.add('is-showing-selection');
      this.trigger.classList.add('is-complete');
      if (this.triggerBadgeEl) this.triggerBadgeEl.style.display = 'none';
    } else {
      this.triggerTextEl.textContent = this.triggerTextEl.dataset.defaultText;
      this.triggerTextEl.classList.remove('is-showing-selection');
      this.trigger.classList.remove('is-complete');
      if (this.triggerBadgeEl) this.triggerBadgeEl.style.display = '';
    }
  }

  updateSubmitState() {
    if (!this.submitButton) return;

    const totalOptions = Object.keys(this.positions).length;
    const chosen = Object.keys(this.selections).length;

    if (chosen < totalOptions) {
      this.submitButton.disabled = true;
      if (this.submitLabel) this.submitLabel.textContent = 'Select options';
    }
  }

  setLoadingState() {
    if (!this.submitButton) return;
    this.submitButton.disabled = true;
    if (this.submitLabel) this.submitLabel.textContent = 'Updating…';
    this.classList.add('is-resolving');
  }

  applyResolvedVariant(data) {
    const form = this.closest('form');
    if (form) {
      const variantInput = form.querySelector('.product-variant-id');
      if (variantInput) {
        variantInput.value = data.variantId;
        variantInput.disabled = !data.available;
      }
    }

    if (data.image) {
      this.showConfigSlide(data.image);
      if (this.previewImg) {
        this.previewImg.src = data.image;
      }
    }

    if (data.price) {
      const priceEl = document.querySelector('.price-item--regular');
      if (priceEl) priceEl.textContent = data.price;
    }

    if (this.submitButton) {
      this.submitButton.disabled = !data.available;
      if (this.submitLabel) {
        this.submitLabel.textContent = data.available ? this.originalSubmitText : 'Sold out';
      }
    }

    this.classList.remove('is-resolving');

    this.dispatchEvent(
      new CustomEvent('gem-picker:variant-change', {
        detail: { variantId: data.variantId },
        bubbles: true,
      })
    );
  }

  async resolveVariant() {
    const optionNames = Object.keys(this.positions).sort(
      (a, b) => this.positions[a] - this.positions[b]
    );

    const ids = optionNames.map((name) => this.selectedIds[name]);
    if (ids.some((id) => !id)) return; // wait until all options are chosen

    const cacheKey = ids.join(',');

    if (this.variantCache[cacheKey]) {
      this.applyResolvedVariant(this.variantCache[cacheKey]);
      return;
    }

    this.setLoadingState();

    const url = `${this.productUrl}?option_values=${cacheKey}&section_id=${this.sectionId}`;

    let html;
    try {
      const res = await fetch(url);
      html = await res.text();
    } catch (err) {
      console.error('[gem-picker] Failed to resolve variant via Section Rendering API:', err);
      this.classList.remove('is-resolving');
      return;
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');

    const resolvedVariantInput = doc.querySelector('.product-variant-id');
    const resolvedImg = doc.querySelector('.product__media img');
    const resolvedPrice = doc.querySelector('.price-item--regular');

    if (!resolvedVariantInput) {
      console.warn('[gem-picker] Section render did not return a matching variant for:', { ...this.selections });
      this.classList.remove('is-resolving');
      return;
    }

    const currentIds = optionNames.map((name) => this.selectedIds[name]);
    if (currentIds.join(',') !== cacheKey) return;

    const data = {
      variantId: resolvedVariantInput.value,
      available: !resolvedVariantInput.disabled,
      image: resolvedImg ? resolvedImg.getAttribute('src') : null,
      price: resolvedPrice ? resolvedPrice.textContent.trim() : null,
    };

    this.variantCache[cacheKey] = data;
    this.applyResolvedVariant(data);
  }
}

customElements.define('product-gem-picker', ProductGemPicker);